import { useState } from "react"
import type { VideoCardData } from "../types"

/** Video card — thumbnail with play overlay by default. Tapping the
 *  thumb swaps in an inline YouTube iframe so the customer can watch
 *  without leaving the chat. A small "Open in YouTube" link on the
 *  iframe stays available for those who want fullscreen / different
 *  controls. */
export function VideoCard({ data }: { data: VideoCardData }) {
  const [playing, setPlaying] = useState(false)

  return (
    <div
      style={{
        background: "rgba(255,255,255,0.04)",
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: 14,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "relative",
          width: "100%",
          aspectRatio: "16 / 9",
          background: "#000",
        }}
      >
        {playing ? (
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${encodeURIComponent(data.videoId)}?autoplay=1&rel=0&modestbranding=1`}
            title={data.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              border: 0,
            }}
          />
        ) : (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            aria-label={`Play video: ${data.title}`}
            style={{
              position: "absolute",
              inset: 0,
              padding: 0,
              border: 0,
              background: "transparent",
              cursor: "pointer",
              display: "block",
            }}
          >
            <img
              src={data.thumbnail}
              alt=""
              loading="lazy"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background:
                  "linear-gradient(180deg, rgba(0,0,0,0) 50%, rgba(0,0,0,0.45) 100%)",
                transition: "background 120ms ease",
              }}
            >
              <div
                style={{
                  width: 54,
                  height: 54,
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.95)",
                  color: "#0d152c",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 4px 18px rgba(0,0,0,0.45)",
                }}
              >
                <svg
                  viewBox="0 0 24 24"
                  width="22"
                  height="22"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M8 5v14l11-7z" />
                </svg>
              </div>
            </div>
          </button>
        )}
      </div>
      <div
        style={{
          padding: "10px 12px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <div
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: 13,
            fontWeight: 600,
            color: "rgba(255,255,255,0.96)",
            lineHeight: 1.3,
            overflow: "hidden",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
          }}
          title={data.title}
        >
          {data.title}
        </div>
        <a
          href={data.url}
          target="_blank"
          rel="noreferrer"
          style={{
            flexShrink: 0,
            fontSize: 11.5,
            fontWeight: 500,
            padding: "5px 11px",
            borderRadius: 999,
            border: "1px solid rgba(255,255,255,0.22)",
            color: "rgba(255,255,255,0.85)",
            textDecoration: "none",
            background: "transparent",
            transition: "background 120ms ease",
          }}
        >
          Open in YouTube
        </a>
      </div>
    </div>
  )
}
